import { Component, effect, inject, OnDestroy, signal } from '@angular/core';
import { CustomRouteService } from './services/custom-route.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { CustomRoute } from './interfaces/custom-route.interface';
import { catchError, of } from 'rxjs';
import { CustomRouteResponseCompleted } from './interfaces/custom-route.response';
import { RegisterUserService } from '../register-user/services/register-user.service';
import { PlannerDayStateService } from './shared/planner/services';
import { RouteDataService } from './shared/map/services/route-data.service';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Box1Component } from "src/app/fitur-26/layout/boxes";
import { PointsComponent } from "./shared/planner/components/route-timeline/components/points/points.component";
import { MapComponent } from "./shared";
import { WebsocketLaravelReverb } from 'src/app/fitur-26/pages/home/services';
import { CHANGE_DAY_ROUTE_EVENT, CHANNEL_ATRIL_PANTALLA_NAME } from 'src/app/fitur-26/pages/home/config';
import { RegisterUserForm } from '../register-user/interfaces';
import { TranslationService } from 'src/app/services';
import { customRouteAdapter } from './adapters/custom-route.adapter';

@Component({
  selector: 'app-custom-route',
  standalone: true,
  imports: [
    TranslateModule,
    Box1Component,
    PointsComponent,
    MapComponent
],
  templateUrl: './custom-route.component.html',
  styleUrls: ['./custom-route.component.scss'],
  host: {
    style: `display: flex; 
            justify-content: center; 
            align-items: center; 
            width: 1100px; 
            height: 100%;
            margin: 0 auto;
          `
  }
})
export class CustomRouteComponent {
  private _router = inject(Router);
  private _activedRoute = inject(ActivatedRoute);
  private _customRouteService = inject(CustomRouteService);
  private _registerUserService = inject(RegisterUserService);
  private _plannerDayStateService = inject(PlannerDayStateService);
  private _routeDataService = inject(RouteDataService);


  protected customRouteData =  this._customRouteService.customRouteData;
  protected isLoading = this._customRouteService.isLoading;
  private _websocketLaravelReverbService = inject(WebsocketLaravelReverb);
  private _translationService = inject(TranslationService);
  
  private _newRouteDayFromWebSocket = signal<number | null>(null);
  
  private _routeToken = this._customRouteService.token;
 
  constructor() {
    this._activedRoute.paramMap.subscribe(params => {
      const routeTokenValue = params.get('routeToken');
      if (routeTokenValue) {
        this._customRouteService.setToken(routeTokenValue);
        this._loadCustomRouteFromRouteToken(routeTokenValue);
      }
    });
  }

  changeRouteDayByWebSocket = effect(() => {
    this._subscribeToChannel();
    const newDayIndex = this._newRouteDayFromWebSocket();
    
    if (newDayIndex !== null) {
      this._plannerDayStateService.setDayToShowIndex(newDayIndex);
    }
  }, { allowSignalWrites: true });
  
  private _subscribeToChannel(): void {
    this._websocketLaravelReverbService
      .subscribeToChannel(CHANNEL_ATRIL_PANTALLA_NAME, CHANGE_DAY_ROUTE_EVENT + this._routeToken())
    .subscribe({
      next: (data: any) => {
        console.log('Evento recibido:', data);
        this._newRouteDayFromWebSocket.set(data[0]);
      },
      error: (err: any) => console.error('Error en canal:', err)
    });
  }

  /**
   * Carga la ruta personalizada desde el token de la ruta.
   * Para rutas cargadas desde el email, se espera que el token esté en la URL
   */
  private _loadCustomRouteFromRouteToken(routeToken: string) {
    this._customRouteService.getCustomRouteResponse(routeToken)
      .pipe(
        catchError((_error) => {
          this._router.navigateByUrl('/home');
          return of(null); 
        })
      )
      .subscribe((res: any) => {
        this._customRouteService.setToken(res.token);

        this._customRouteService.setCurrentOrder(res.dailyRoutes?.length);
        
        this._registerUserService.setNumberOfDaysCustomRoute(res.dailyRoutes?.length);
        
        this._translationService.setTranslateService(res.language || 'es');

        // add all the daily routes to the customRouteData
        res.dailyRoutes?.forEach((_, index) => {
          const args: CustomRouteResponseCompleted = {
            status: 200,
            id: res.id,
            token: res.token,
            language: res.language,
            dailyRoutes: res.dailyRoutes[index],
            stats: res.stats
          }
          this._customRouteService.addDailyRoute(args); 
        })
    
        const customRoute = customRouteAdapter(
          res.id,
          res.dailyRoutes,
          res.token,
          res.language,
          res.stats
        );
        
        this._customRouteService.setCustomRouteData(customRoute);
        this._customRouteService.setRouteUpdated(true);
      }
    )
  }
  
  protected goToDetailPage(pointId: number, pointType: string, from: string): void {
    const typeNormalized = pointType === 'event' ? 'event' : 'point';
    
    this._router.navigate(
      ['/home', typeNormalized, pointId], 
      { queryParams: { from } }
    );
  }
}
